import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import ErrorPage from 'app/components/Error/ErrorPage';
import {ReportFeatureInfo} from 'app/components/ReportExport/ReportExportFeature';
import {ReportExportWindowContents} from 'app/components/ReportExport/ReportExportWindow';
import {api} from 'app/modules/Remote';
import {ApiPublicLensResponse, ApiPublicNote, ApiPublicReportMeta} from 'app/modules/Remote/api';
import {ApiFeature, ApiFeatureData, ApiReport, ReportState} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, TagSetting} from 'app/modules/Remote/Organization';
import {DEFAULT_COLORS} from 'app/pages/MonitorProjectView/SwatchColorPicker';
import {GeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import {StateApiNoteWithTagSettings} from 'app/stores/NotesStore';
import {HideHubspotWidget} from 'app/tools/Hubspot';
import * as layerUtils from 'app/utils/layerUtils';
import {getMultiFeaturePropertyParts} from 'app/utils/multiFeaturePropertyUtils';
import * as noteUtils from 'app/utils/noteUtils';
import {makeShareLinkOverlaySettings} from 'app/utils/overlayUtils';
import * as tagUtils from 'app/utils/tagUtils';

/**
 * Overlays render from their feature collection's PMTiles, so a report never loads overlay
 * geometry. useGeoJsonFeaturesLoader's contract still has to be met for the report tree.
 */
const NO_GEOJSON_OVERLAYS: GeoJsonFeaturesLoader = () =>
  I.List() as unknown as I.ImmutableOf<ApiFeature[]>;

/**
 * The `/p/<uuid>` payload of a link that points at a report. PublicReportPage narrows to
 * this; a payload without `report` belongs to the map route.
 */
export type ReportSharePayload = ApiPublicLensResponse & {report: ApiPublicReportMeta};

interface Props {
  uuid: string;
  payload: ReportSharePayload;
}

/**
 * A saved report, rendered for someone with no Lens account.
 *
 * Does its own loading rather than reusing ReportExportWindow: those loaders go through the
 * logged-in endpoints and providers, and none of that exists here. The public routes under
 * /p/<uuid> serve the same shapes, so the report renderer itself is unchanged.
 */
const PublicReport: React.FunctionComponent<Props> = ({uuid, payload}) => {
  const reportMeta = payload.report;

  const organization = React.useMemo(
    () => I.fromJS(payload.organization) as I.ImmutableOf<ApiOrganization>,
    [payload.organization]
  );
  const features = React.useMemo(
    () => I.fromJS(payload.features) as I.ImmutableListOf<ApiFeature>,
    [payload.features]
  );
  const featureCollection = React.useMemo(
    () => I.fromJS(payload.feature_collection) as I.ImmutableOf<ApiFeatureCollection>,
    [payload.feature_collection]
  );
  const overlayFeatureCollections = React.useMemo(
    () => I.fromJS(payload.overlay_feature_collections) as I.ImmutableOf<ApiFeatureCollection[]>,
    [payload.overlay_feature_collections]
  );
  const overlaySettings = React.useMemo(
    () =>
      makeShareLinkOverlaySettings(
        payload.overlay_feature_collections,
        payload.overlay_configs ?? [],
        DEFAULT_COLORS
      ),
    [payload.overlay_feature_collections, payload.overlay_configs]
  );

  const selectedFeature = React.useMemo(
    () => features.find((f) => f!.getIn(['properties', 'id']) === reportMeta.featureId)!,
    [features, reportMeta.featureId]
  );

  const propertyParts = React.useMemo(() => {
    const parts = getMultiFeaturePropertyParts(features, selectedFeature);
    // With every part in the report, none of them is the "active" one to highlight.
    return reportMeta.isMultilocation ? parts.map((p) => ({...p, isActive: false})) : parts;
  }, [features, selectedFeature, reportMeta.isMultilocation]);

  const partFeatures = React.useMemo(
    () => (reportMeta.isMultilocation ? propertyParts.map((p) => p.feature) : [selectedFeature]),
    [reportMeta.isMultilocation, propertyParts, selectedFeature]
  );

  const [savedReport, setSavedReport] = React.useState<ApiReport<ReportState> | null>(null);
  const [featureInfos, setFeatureInfos] = React.useState<ReportFeatureInfo[] | null>(null);
  const [loadError, setLoadError] = React.useState(false);

  const tagSettings = React.useMemo(
    () => tagUtils.getTagSettings(organization, tagUtils.TAG_KIND.NOTE).toJS(),
    [organization]
  ) as Record<string, TagSetting>;

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [report, infos] = await Promise.all([
          api.public.report(uuid).then((r) => r.get('data').toJS() as ApiReport<ReportState>),
          Promise.all(
            partFeatures.map(async (feature) => {
              const featureId = feature.getIn(['properties', 'id']) as number;
              const [notes, featureData] = await Promise.all([
                api.public
                  .featureNotes(uuid, featureId)
                  .then((r) => r.get('data').toJS() as ApiPublicNote[]),
                api.public
                  .featureData(uuid, featureId)
                  .then((r) => r.get('data') as I.ImmutableOf<ApiFeatureData[]>),
              ]);
              return {
                feature,
                featureData,
                notes: notes.map((note) => hydratePublicNote(note, tagSettings)),
              };
            })
          ),
        ]);

        if (!cancelled) {
          setSavedReport(report);
          setFeatureInfos(infos);
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [uuid, partFeatures, tagSettings]);

  if (loadError) {
    return (
      <>
        <ErrorPage
          message="This content is no longer available."
          linkTo="https://www.upstream.tech/lens"
        />
        <div id="report-error" data-testid="report-error" aria-hidden />
      </>
    );
  }

  if (!savedReport || !featureInfos) {
    return (
      <div style={{width: '30%', margin: '10rem auto'}}>
        <B.ProgressBar intent="primary" />
      </div>
    );
  }

  return (
    <>
      <ReportExportWindowContents
        structure="document"
        readOnly
        firebaseToken=""
        organization={organization}
        projectId=""
        geoJsonFeaturesLoader={NO_GEOJSON_OVERLAYS}
        overlayFeatureCollections={overlayFeatureCollections}
        overlaySettings={overlaySettings}
        filterNotesCallback={() => true}
        featureInfos={featureInfos}
        propertyParts={propertyParts}
        totalCount={partFeatures.length}
        loadedCount={featureInfos.length}
        enrolledLayerKeys={layerUtils.getEnrolledLayerKeysForFeatureCollection(featureCollection)}
        savedReport={savedReport}
        savedReportId={reportMeta.id}
        reportTitle={reportMeta.title}
        reportWindow={null}
        selectedFeature={selectedFeature}
      />
      <HideHubspotWidget />
    </>
  );
};

/**
 * The extra fields the note renderer expects on top of what the API returns. Mirrors
 * NotesActionsImpl#hydrateNote, minus the parts that need a signed-in profile: nobody
 * viewing a share link authors anything, and the display name arrives on the note itself.
 */
function hydratePublicNote(
  note: ApiPublicNote,
  tagSettings: Record<string, TagSetting>
): StateApiNoteWithTagSettings {
  return {
    ...note,
    isCreating: false,
    isUpdating: false,
    isAuthor: false,
    name: note.userName ?? null,
    imageRefs: noteUtils.makeImageRefsFromNote(note),
    // A note can carry a tagId the org has since removed, so filter to tags that still exist.
    tagSettings: note.tagIds
      ? Object.keys(note.tagIds)
          .filter((tagId) => tagSettings[tagId])
          .map((tagId) => tagSettings[tagId])
      : null,
    // The public note is deliberately narrower than the logged-in one: userId is the
    // author's email address, and organizationId/projectId say nothing a viewer needs.
    // Nothing the report renders reads them.
  } as unknown as StateApiNoteWithTagSettings;
}

export default PublicReport;
